import cloneDeep from 'lodash/cloneDeep';
import Errored from './Errored';
import { Typography } from '@material-ui/core';
import ActionText from '../core/ActionText';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import GS from '../grafsync/grafsync';
import { globalStyles } from './style/gstheme';
import { createLengthWarning, debounce, makeCancelable, checkValidForDatatype, sanitiseText } from './utils/utils';

const styles = theme => {
    return Object.assign({
        showing: {
            marginTop: 6,
            fontSize: '12px',
            paddingLeft: 6,
            color: theme.palette.primary.main
        },
        unselectedResult: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            borderRadius: 4,
            alignItems: 'flex-start',
            cursor: 'pointer',
            backgroundColor: '#f8f8f8',
            '&:hover': {
                backgroundColor: '#e0e0e0'
            }
        },
        unselectedResultHover: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            borderRadius: 4,
            alignItems: 'flex-start',
            cursor: 'pointer',
            backgroundColor: '#e0e0e0'
        },
        selectedResult: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            alignItems: 'flex-start',
            color: '#212121',
            opacity: 0.6
        },
        searchBox: {
            height: 'calc(100% - 4px)',
            paddingTop: 4,
            overflow: 'hidden'
        },
        searchResults: {
            margin: '12px 0px 0px 0px',
            paddingRight: 6,
            height: 'calc(100% - 100px)',
            overflowY: 'scroll'
        }
    }, globalStyles);
};

class SearchSelect extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isSearching: false,
            highlighted: -1,
            searchPending: false,
            results: [],
            slowSearch: false,
            scroll: false,
            searchCount: null,
            searchID: null,
            text: this.props.initialText,
            disableEmptySearches: false,
            internalErrorText: "",
            isErrored: null
        };
        this.textInput = React.createRef();
        this.cancellable = null;
        this.resourceRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.allowEmpty || this.state.text !== "") {
            this.setState({ searchPending: true }, () => {
                this.find(this.state.text, this.props.lang);
            });
        }
    }

    componentDidUpdate() {
        if (this.state.scroll && this.resourceRef && this.resourceRef.current) {
            this.resourceRef.current.scrollIntoView({
                block: 'center',
                inline: 'center',
            });
            this.setState({ scroll: false });
        }
    }

    find = (value, lang) => {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isSearching: true, slowSearch: false }, async () => {
            try {
                this.beingSlow();
                const searchPayload = this.props.search(value, lang);
                const startTime = Date.now();
                const data = await this.doSearch(searchPayload).promise;
                const endTime = Date.now();
                if (endTime - startTime > 20000) {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.setState({ disableEmptySearches: true });
                }
                if (this.slowTimeout) clearTimeout(this.slowTimeout);
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // Ignore 'old' searches or if text has changed. Moot due to debounce
                if (data.uuid && data.uuid !== this.state.searchID) return;
                if (value !== this.state.text) {
                    this.setState({ isSearching: false });
                    return;
                }
                this.setState({
                    isSearching: false,
                    searchPending: false,
                    results: this.state.text !== "" || this.props.allowEmpty ? data.results : [],
                    pageSize: searchPayload.pageSize,
                    searchCount: (this.state.text !== "" || this.props.allowEmpty) && data.searchCount ? data.searchCount : null
                });
                if ((!data.results || data.results.length === 0) && this.props.onNoResults && value !== "") {
                    if (this.props.allowIRIonNoResults) {
                        let isValidForUse = this.state.internalErrorText === "";
                        if (isValidForUse && searchPayload.graph && Array.isArray(searchPayload.graph)) {
                            const isValid = checkValidForDatatype(value, "http://www.w3.org/2001/XMLSchema#anyURI");
                            try {
                                // Change this to use basicCheckIRI
                                decodeURI(value);
                                const url = new URL(value);
                                if (url.hash && url.hash.substring(1).includes('#')) return;
                                if (url.hash.includes('"')) return;
                            }
                            catch (e) {
                                return;
                            }
                            if (isValid) {
                                const checkIsValidForExternal = await this.doFind({
                                    value,
                                    deleted: true,
                                    graph: searchPayload.graph[0]
                                }).promise;
                                if (this.cancellable && this.cancellable.isCancelled()) return;
                                isValidForUse = !checkIsValidForExternal.exists;
                                if (!isValidForUse) {
                                    this.setState({ internalErrorText: "This IRI is not valid for use. It is a deleted IRI." });
                                }
                            }
                        }
                        if (this.props.onNoResults) this.props.onNoResults(value, isValidForUse);
                    }
                    else {
                        if (this.props.onNoResults) this.props.onNoResults(value, this.state.internalErrorText === "");
                    }
                }
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                    this.setState({
                        isErrored: e,
                        isSearching: false,
                        searchPending: false,
                        searchCount: null,
                        internalErrorText: "",
                        results: []
                    });
                }
            }
        });
    };

    beingSlow = () => {
        if (this.slowTimeout) clearTimeout(this.slowTimeout);
        this.slowTimeout = setTimeout(() => {
            this.setState({ slowSearch: true, disableEmptySearches: true });
        }, 20000);
    };

    handleChange = (event) => {
        if (this.props.onClearResults) this.props.onClearResults();
        const text = sanitiseText(event.target.value);
        if (text.length < 2 && this.state.disableEmptySearches) {
            this.setState({ internalErrorText: "", text, highlighted: -1, isSearching: false, results: [], searchPending: false, searchCount: null, searchID: 0 });
            return;
        }
        if (this.props.updateSearchText) this.props.updateSearchText(text);
        if (text !== this.state.text) {
            this.setState({ internalErrorText: "", text, searchPending: true, searchCount: null, highlighted: -1 });
            if ((text === "" && !this.props.allowEmpty)
                || (this.props.exclude && this.props.exclude.includes(text))) {
                const internalErrorText = this.props.exclude && this.props.exclude.includes(text) ?
                    "This value already exists or is the source IRI."
                    :
                    "";
                this.setState({ results: [], searchPending: false, searchCount: null, internalErrorText, searchID: 0 });
                if (this.props.onNoResults) this.props.onNoResults(text, false);
                return;
            }
            debounce(this.find, this, [text, this.props.lang]);
        }
    };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    doSearch(payload) {
        const searchID = uuidv4();
        this.setState({ searchID });
        this.cancellable = makeCancelable(GS.search(payload, searchID));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        if (this.slowTimeout) clearTimeout(this.slowTimeout);
    }

    chosen = (id, resourceType) => {
        let newResults = cloneDeep(this.state.results);
        if (this.props.singleValue) {
            newResults.forEach(result => {
                if (result.invalid) delete result.invalid;
            });
        }
        const chosenResult = newResults.find(result => result.id === id);
        chosenResult.invalid = true;
        this.setState({ results: newResults });
        this.props.chosen(id, resourceType);
    };

    handleCursor = (event) => {
        const hl = this.state.highlighted;
        switch (event.keyCode) {
            case 13:
                if (hl !== -1) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.chosen(this.state.results[hl].id, this.state.results[hl].type);
                }
                break;
            case 38:
                event.preventDefault();
                event.stopPropagation();
                if (hl > 0) this.setState({ highlighted: hl - 1, scroll: true });
                break;
            case 40:
                event.preventDefault();
                event.stopPropagation();
                if (hl < this.state.results.length - 1) this.setState({ highlighted: hl + 1, scroll: true });
                break;
            default:
        }
    };

    clearError = () => this.setState({ isErrored: false });

    render() {

        const { results, isSearching, text, pageSize, searchCount, searchPending, highlighted,
            disableEmptySearches, isErrored, internalErrorText, slowSearch } = this.state;
        const { classes, lang, title, listDisplay, errorText, suppressMessages, dataTest, maxLength, exclude, sortByLength,
            sortProp, listSecondIcon, listIcon, focus, isClickableResult } = this.props;

        if (isErrored) return <Errored data={isErrored} dataTest="ss-error" clearError={this.clearError} />;

        return (
            <div className={classes.searchBox} onKeyDown={this.handleCursor}>
                <TextField
                    style={{ width: '100%', marginTop: '0px' }}
                    margin="dense"
                    autoFocus={focus}
                    inputRef={this.textInput}
                    type="text"
                    value={text}
                    onChange={this.handleChange}
                    error={errorText !== null || internalErrorText !== ""}
                    placeholder={title}
                    variant="outlined"
                    helperText={!suppressMessages ?
                        errorText || internalErrorText ?
                            <span data-test="helper-text">{errorText || internalErrorText}</span>
                            :
                            createLengthWarning(text, maxLength)
                        :
                        null
                    }
                    InputLabelProps={{
                        className: classes.inputLabel,
                        classes: {
                            shrink: classes.inputLabelShrink
                        }
                    }}
                    InputProps={
                        {
                            className: classes.inputLabel,
                            inputProps: {
                                "data-test": dataTest + "-input",
                                maxLength
                            },
                            endAdornment:
                                <InputAdornment position="end">
                                    {!isSearching && text !== "" ?
                                        <Tooltip title="Clear">
                                            <span>
                                                <ClearIcon className={classes.inputIconHighlighted} onClick={
                                                    () => {
                                                        this.handleChange({
                                                            target: {
                                                                value: ""
                                                            }
                                                        });
                                                        this.textInput && this.textInput.current && this.textInput.current.focus();
                                                    }
                                                } />
                                            </span>
                                        </Tooltip>
                                        :
                                        (isSearching ?
                                            <Tooltip title="Searching for results">
                                                <span>
                                                    <CircularProgress color="secondary" size={20} />
                                                </span>
                                            </Tooltip>
                                            :
                                            <Tooltip title="Search">
                                                <span>
                                                    <SearchIcon className={classes.inputIconHighlighted} />
                                                </span>
                                            </Tooltip>
                                        )
                                    }
                                </InputAdornment>
                        }
                    } />
                {disableEmptySearches &&
                    <Typography
                        data-test="disabled-empty"
                        variant="caption"
                        className={classes.showing}>
                        <span>Empty searches are disabled for performance.</span>
                    </Typography>
                }
                {!isSearching && pageSize && searchCount &&
                    <Typography
                        data-test="showing-results"
                        variant="body2"
                        className={classes.showing}>
                        <span>Showing {pageSize < searchCount ? pageSize : searchCount} of {searchCount} results.</span>
                        {pageSize < searchCount && <span> Filter to refine results.</span>}
                    </Typography>
                }
                {results && results.length > 0 && !isSearching ?
                    <List data-test="list-results" dense className={classes.searchResults}>
                        {
                            results.sort(
                                function (a, b) {
                                    const sortProp1 = Array.isArray(sortProp) ? sortProp[0] : sortProp;
                                    const sortProp2 = Array.isArray(sortProp) && sortProp.length === 2 ? sortProp[1] : null;
                                    // Sort by length first
                                    if (sortByLength) {
                                        const lenA = sortProp1 && a[sortProp1] && a[sortProp1][lang] ?
                                            a[sortProp1][lang].length
                                            :
                                            (sortProp2 && a[sortProp2] && a[sortProp2][lang] ? a[sortProp2][lang].length : a.id.length);
                                        const lenB = sortProp1 && b[sortProp1] && b[sortProp1][lang] ?
                                            b[sortProp1][lang].length
                                            :
                                            (sortProp2 && b[sortProp2] && b[sortProp2][lang] ? b[sortProp2][lang].length : b.id.length);
                                        if (lenA < lenB) return -1;
                                        if (lenA > lenB) return 1;
                                    }
                                    // Then sort by prop value - we do upper case to force IRIs to come at the end
                                    const nameA = sortProp1 && a[sortProp1] && a[sortProp1][lang] ?
                                        a[sortProp1][lang].toString().toUpperCase()
                                        :
                                        (sortProp2 && a[sortProp2] && a[sortProp2][lang] ? a[sortProp2][lang].toString().toUpperCase() : a.id);
                                    const nameB = sortProp1 && b[sortProp1] && b[sortProp1][lang] ?
                                        b[sortProp1][lang].toString().toUpperCase()
                                        :
                                        (sortProp2 && b[sortProp2] && b[sortProp2][lang] ? b[sortProp2][lang].toString().toUpperCase() : b.id);
                                    if (nameA < nameB) return -1;
                                    if (nameB < nameA) return 1;
                                    // Finally sort by prop names
                                    const propA = sortProp1 && a[sortProp1] ?
                                        sortProp1
                                        :
                                        (sortProp2 && a[sortProp2] ? sortProp2 : "");
                                    const propB = sortProp1 && b[sortProp1] ?
                                        sortProp1
                                        :
                                        (sortProp2 && b[sortProp2] ? sortProp2 : "");
                                    if (propA < propB) return -1;
                                    if (propB < propA) return 1;
                                    return 0;
                                }
                            ).map((result, index) => {
                                let clickable = true;
                                if (isClickableResult) {
                                    clickable = exclude ?
                                        !exclude.includes(result.id) && isClickableResult(result)
                                        :
                                        true;
                                }
                                if (clickable) {
                                    return <ListItem key={result.id}
                                        ref={highlighted === index ? this.resourceRef : null}
                                        onClick={() => this.chosen(result.id, result.type)}
                                        data-test={"list-item-" + index}
                                        className={highlighted === index ? classes.unselectedResultHover : classes.unselectedResult}>
                                        {listIcon && <ListItemIcon className={classes.resultIcon}>{listIcon(result, lang)}</ListItemIcon>}
                                        <ListItemText disableTypography data-test="list-item-text" primary={listDisplay(result, lang)} />
                                        {listSecondIcon && <ListItemSecondaryAction className={classes.resultSecondIcon}>{listSecondIcon(result, lang)}</ListItemSecondaryAction>}
                                    </ListItem>;
                                }
                                else {
                                    return <ListItem
                                        ref={highlighted === index ? this.resourceRef : null}
                                        data-test={"list-item-" + index + "-invalid"}
                                        className={classes.selectedResult}
                                        key={result.id}>
                                        {listIcon && <ListItemIcon className={classes.resultIcon}>{listIcon(result, lang)}</ListItemIcon>}
                                        <ListItemText disableTypography data-test="list-item-text" primary={listDisplay(result, lang)} />
                                        {listSecondIcon && <ListItemSecondaryAction className={classes.resultSecondIcon}>{listSecondIcon(result, lang)}</ListItemSecondaryAction>}
                                    </ListItem>;
                                }
                            })}
                    </List>
                    :
                    <div data-test="no-results" >
                        {!isSearching && !searchPending &&
                            <ActionText offset="100px">{text === "" && !this.props.allowEmpty ?
                                "Enter text to search."
                                :
                                "No results."
                            }</ActionText>
                        }
                        {searchPending && <ActionText offset="100px">
                            <span style={{display: 'block'}}>Searching ...</span>
                            {slowSearch && text === "" && <span style={{display: 'block'}}>Hmmm, taking a while! Try entering some text.</span>}
                        </ActionText>}
                    </div>
                }
            </div>
        );
    }
}

SearchSelect.defaultProps = {
    focus: false,
    suppressMessages: false,
    errorText: null,
    maxLength: 50,
    allowEmpty: true,
    initialText: "",
    sortByLength: false,
    allowIRIonNoResults: false,
    singleValue: false,
    selectedStyle: {},
    exclude: []
};

SearchSelect.propTypes = {
    dataTest: PropTypes.string,
    lang: PropTypes.string,
    maxLength: PropTypes.number,
    focus: PropTypes.bool,
    margin: PropTypes.bool,
    singleValue: PropTypes.bool,
    allowIRIonNoResults: PropTypes.bool,
    initialText: PropTypes.string,
    exclude: PropTypes.array,
    title: PropTypes.string,
    isClickableResult: PropTypes.func,
    chosen: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
    sortByLength: PropTypes.bool,
    selectedStyle: PropTypes.object,
    listIcon: PropTypes.func,
    allowEmpty: PropTypes.bool,
    listSecondIcon: PropTypes.func,
    sortProp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
    ]),
    updateSearchText: PropTypes.func,
    errorText: PropTypes.string,
    suppressMessages: PropTypes.bool,
    listDisplay: PropTypes.func.isRequired,
    onClearResults: PropTypes.func,
    onNoResults: PropTypes.func,
    classes: PropTypes.object
};

export default withStyles(styles)(SearchSelect);